import { createApi } from "@reduxjs/toolkit/query/react";
import { AxiosError } from "axios";
import { axiosNewPrivate } from "../../util/axios";
import { serializeQueryArgsWithTenant } from "../tenantCacheKey";

/**
 * M1 (#3035) — the rooms of one product category, undated, from
 * `GET /app/categories/:key/rooms` (new-backend/app). Resolved URL:
 * `REACT_APP_NEW_API_BASE_URL` (…/app) + `categories/<key>/rooms` →
 * `…/app/categories/<key>/rooms`.
 *
 * The first screen of an object-first category. Like the room week view, the
 * answer depends on WHO asks (a members-only room is listed to its
 * organisations' members only), so it takes the same two guards:
 *   - `viewerId` is part of the argument — a different account is a different
 *     cache entry by construction;
 *   - the slice is ACCOUNT-SCOPED (`redux/accountScopedCaches.ts`), so a login
 *     or logout evicts it.
 * The tenant is in the key via `serializeQueryArgsWithTenant`.
 *
 * No price and no availability: those depend on dates, which the time step
 * asks for afterwards.
 */
export interface CategoryRoom {
  id: string;
  groupId: string;
  assetId: string;
  name: string;
  langNames: Record<string, { title: string; description: string }> | null;
  description: string;
  featuredImage: string | null;
  compartmentTypes: string[];
  maxLimit: number | null;
  locationId: string | null;
  city: string | null;
  district: string | null;
  geoLat: number | null;
  geoLng: number | null;
  accomodationdetail: Record<string, any>;
  isWholeRoom: boolean;
  partOfRoom: {
    name: string;
    langNames?: Record<string, { title: string; description: string }> | null;
  } | null;
}

export interface CategoryRoomsArgs {
  categoryKey: string;
  /** The signed-in account id, or null — part of the cache key on purpose. */
  viewerId: string | null;
}

export const categoryRoomsApiSlice = createApi({
  reducerPath: "categoryRoomsApiSlice",
  // Every call goes through the queryFn below; the base query is never hit.
  baseQuery: async () => ({ data: undefined }),
  serializeQueryArgs: serializeQueryArgsWithTenant,
  tagTypes: [],
  endpoints: (builder) => ({
    getCategoryRooms: builder.query<CategoryRoom[], CategoryRoomsArgs>({
      queryFn: async ({ categoryKey }) => {
        try {
          const result = await axiosNewPrivate.get<{ items?: unknown }>(
            `categories/${encodeURIComponent(categoryKey)}/rooms`
          );
          // A 200 without a list is a failure, not an empty catalogue — the
          // two must reach the customer as different screens (#1970).
          const items = result.data?.items;
          if (!Array.isArray(items)) {
            return {
              error: { status: "PARSING_ERROR", data: { message: "the room list came back without rooms" } },
            };
          }
          return { data: items as CategoryRoom[] };
        } catch (err) {
          const axiosErr = err as AxiosError<{ message?: string }>;
          return {
            error: {
              status: axiosErr.response?.status ?? "FETCH_ERROR",
              data: axiosErr.response?.data ?? { message: axiosErr.message },
            },
          };
        }
      },
    }),
  }),
});

export const { useGetCategoryRoomsQuery } = categoryRoomsApiSlice;
